import { useEffect, useState } from 'react';
import useCallApiAndLoad from '../../../hooks/useCallApiAndLoad';
import useAuth from '../../../hooks/useAuth';
import { fetchResponseAdapter } from '../../../adapters/fetchAdapter';
import { getVisitFeedbackEndpoint } from '../services/dashboardService';
import { FeedbackState } from './usePreSaleVisitFeedback';

export interface VisitFeedbackData {
  interest_level: string;
  objections: string[];
  property_condition: string[];
  owner_recommendation: string;
  move_in_timeframe?: string | null;
  logistics_issue?: { had_issue: boolean; details?: string | null } | null;
}

// Feedback del Proper de una visita de Renta/Venta. 404 = aún sin feedback.
// Código del 404 de la ruta de admin cuando la visita no tiene feedback.
const FEEDBACK_NOT_FOUND_CODE = 1108;

const useVisitFeedback = (
  visitId?: string,
  enabled = true,
  asAdmin = false,
) => {
  const { callEndpoint } = useCallApiAndLoad();
  const { getAuthToken } = useAuth();
  const token = getAuthToken();
  const [state, setState] = useState<FeedbackState>('loading');
  const [feedback, setFeedback] = useState<VisitFeedbackData | null>(null);

  useEffect(() => {
    if (!visitId || !enabled) return undefined;
    let active = true;
    setState('loading');
    (async () => {
      const { status, data } = await callEndpoint(
        getVisitFeedbackEndpoint(visitId, token, asAdmin),
      );
      if (!active) return;
      if (status === 200) {
        setFeedback(fetchResponseAdapter(data).data);
        setState('done');
      } else if (
        status === 404 &&
        // En la ruta de admin sólo 1108 significa "sin feedback"; otro 404
        // (1102, ruta no desplegada) es un error.
        (!asAdmin || fetchResponseAdapter(data).code === FEEDBACK_NOT_FOUND_CODE)
      ) {
        setFeedback(null);
        setState('pending');
      } else {
        setState('error');
      }
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visitId, enabled, asAdmin]);

  return { state, feedback };
};

export default useVisitFeedback;
